import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Ban, RotateCcw, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge, type BadgeVariant } from '../components/ui/Badge';
import { Dialog } from '../components/ui/Dialog';
import { Input, Textarea, Label } from '../components/ui/Input';
import { PageLoader } from '../components/ui/Spinner';
import { apiFetch } from '../lib/apiFetch';
import { formatDate, formatDateTime, formatUzs } from '../lib/format';
import { useT } from '../i18n';

interface UserCardData {
  user: {
    id: number;
    telegram_id: number;
    username: string | null;
    first_name: string | null;
    full_name: string | null;
    age: number | null;
    region_code: string | null;
    phone: string | null;
    status_code: string;
    admin_role: string | null;
    is_blocked: boolean;
    rules_accepted_at: string | null;
    created_at: string;
  };
  subscriptions: Array<{
    id: number;
    course_id: number;
    course_title: string;
    starts_at: string;
    ends_at: string;
    is_active: boolean;
    cancelled_at: string | null;
    cancel_reason: string | null;
  }>;
  payments: Array<{
    id: number;
    course_title: string;
    amount_uzs: string;
    status_code: string;
    submitted_at: string;
    reviewed_at: string | null;
  }>;
}

const PAYMENT_VARIANT: Record<string, BadgeVariant> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'destructive',
  cancelled: 'muted',
};

export function UserCard() {
  const { id } = useParams<{ id: string }>();
  const userId = Number(id);
  const qc = useQueryClient();
  const t = useT();

  const [extending, setExtending] = useState<{ subId: number; title: string } | null>(null);
  const [extendDays, setExtendDays] = useState('30');
  const [blocking, setBlocking] = useState(false);
  const [blockReason, setBlockReason] = useState('');

  const card = useQuery({
    queryKey: ['user-card', userId],
    queryFn: () => apiFetch<UserCardData>(`/api/admin/users/${userId}`),
    enabled: Number.isFinite(userId),
  });

  const extend = useMutation({
    mutationFn: ({ subId, days }: { subId: number; days: number }) =>
      apiFetch(`/api/admin/users/${userId}/extend`, {
        method: 'POST',
        body: { subscription_id: subId, days },
      }),
    onSuccess: () => {
      setExtending(null);
      qc.invalidateQueries({ queryKey: ['user-card', userId] });
    },
  });

  const block = useMutation({
    mutationFn: (reason: string) =>
      apiFetch(`/api/admin/users/${userId}/block`, { method: 'POST', body: { reason } }),
    onSuccess: () => {
      setBlocking(false);
      setBlockReason('');
      qc.invalidateQueries({ queryKey: ['user-card', userId] });
    },
  });

  const unblock = useMutation({
    mutationFn: () => apiFetch(`/api/admin/users/${userId}/unblock`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user-card', userId] }),
  });

  if (card.isLoading) return <PageLoader />;
  if (!card.data) return <p className="text-slate-500">{t('userCard.notFound')}</p>;
  const { user, subscriptions, payments } = card.data;

  return (
    <div className="space-y-4">
      <Link to="/users" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
        <ChevronLeft className="h-4 w-4" />
        {t('userCard.backToList')}
      </Link>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>{user.full_name ?? user.first_name ?? '—'}</CardTitle>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
              {user.username && <span>@{user.username}</span>}
              <span className="font-mono">{user.telegram_id}</span>
              <Badge variant={user.is_blocked ? 'destructive' : 'success'}>{user.status_code}</Badge>
              {user.admin_role && <Badge variant="info">{user.admin_role}</Badge>}
            </div>
          </div>
          <div className="flex gap-2">
            {user.is_blocked ? (
              <Button size="sm" variant="success" onClick={() => unblock.mutate()} loading={unblock.isPending}>
                <RotateCcw className="h-4 w-4" />
                {t('userCard.unblock')}
              </Button>
            ) : (
              <Button size="sm" variant="destructive" onClick={() => setBlocking(true)}>
                <Ban className="h-4 w-4" />
                {t('userCard.block')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
          <Field label={t('userCard.age')} value={user.age?.toString() ?? '—'} />
          <Field label={t('users.region')} value={user.region_code ?? '—'} />
          <Field label={t('userCard.phone')} value={user.phone ?? '—'} />
          <Field label={t('users.registeredAt')} value={formatDate(user.created_at)} />
          <Field label={t('userCard.rulesAcceptedAt')} value={formatDateTime(user.rules_accepted_at)} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('userCard.subscriptions', { count: subscriptions.length })}</CardTitle>
        </CardHeader>
        <CardContent>
          {subscriptions.length === 0 ? (
            <p className="text-sm text-slate-500">{t('userCard.noSubscriptions')}</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {subscriptions.map((s) => (
                <li key={s.id} className="flex items-center justify-between py-3">
                  <div>
                    <div className="font-medium text-slate-900">{s.course_title}</div>
                    <div className="text-sm text-slate-500">
                      {formatDate(s.starts_at)} → {formatDate(s.ends_at)}
                      {s.is_active ? (
                        <Badge variant="success" className="ml-2">{t('userCard.subActive')}</Badge>
                      ) : (
                        <Badge variant="muted" className="ml-2">{t('userCard.subFinished')}</Badge>
                      )}
                    </div>
                    {s.cancel_reason && (
                      <div className="mt-1 text-xs text-slate-500">
                        {t('userCard.cancelReason', { reason: s.cancel_reason })}
                      </div>
                    )}
                  </div>
                  {s.is_active && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setExtending({ subId: s.id, title: s.course_title })}
                    >
                      <Calendar className="h-4 w-4" />
                      {t('userCard.extend')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('userCard.paymentHistory', { count: payments.length })}</CardTitle>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <p className="text-sm text-slate-500">{t('userCard.noPayments')}</p>
          ) : (
            <ul className="divide-y divide-slate-100 text-sm">
              {payments.map((p) => (
                <li key={p.id} className="flex items-center justify-between py-2">
                  <div>
                    <span className="font-mono text-slate-500">#{p.id}</span> · {p.course_title}
                  </div>
                  <div className="flex items-center gap-3">
                    <span>{formatUzs(p.amount_uzs)}</span>
                    <Badge variant={PAYMENT_VARIANT[p.status_code] ?? 'default'}>{p.status_code}</Badge>
                    <span className="text-xs text-slate-500">{formatDateTime(p.submitted_at)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!extending}
        onClose={() => setExtending(null)}
        title={t('userCard.extendTitle', { title: extending?.title ?? '' })}
        description={t('userCard.extendDescription')}
      >
        <div className="space-y-3">
          <Label htmlFor="days">{t('common.days')}</Label>
          <Input id="days" type="number" min="1" max="365" value={extendDays} onChange={(e) => setExtendDays(e.target.value)} />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setExtending(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={extend.isPending}
              onClick={() => extending && extend.mutate({ subId: extending.subId, days: Number(extendDays) || 30 })}
            >
              {t('userCard.extend')}
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={blocking}
        onClose={() => setBlocking(false)}
        title={t('userCard.blockTitle')}
        description={t('userCard.blockDescription')}
      >
        <div className="space-y-3">
          <Label htmlFor="reason">{t('userCard.blockReason')}</Label>
          <Textarea id="reason" value={blockReason} onChange={(e) => setBlockReason(e.target.value)} rows={3} />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setBlocking(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={!blockReason.trim()}
              loading={block.isPending}
              onClick={() => block.mutate(blockReason.trim())}
            >
              {t('userCard.block')}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-slate-900">{value}</div>
    </div>
  );
}
